import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, TextInput, Select, Label } from '../../';
import stewardReportServices from '../../../services/stewardReportServices';
import { StewardReportHeader } from '../../../services/dtos';

interface ReportHeaderBarProps {
  header: StewardReportHeader;
  onHeaderUpdate: (header: StewardReportHeader) => void;
  isReadOnly: boolean;
}

export const ReportHeaderBar: FC<ReportHeaderBarProps> = ({
  header,
  onHeaderUpdate,
  isReadOnly,
}) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    reportBeginDate: header.reportBeginDate || '',
    reportEndDate: header.reportEndDate || '',
    reportToLocation: header.reportToLocation,
    reportToPerson: header.reportToPerson,
  });

  const svc = new stewardReportServices();

  const formatDateRange = (): string => {
    if (!header.reportBeginDate || !header.reportEndDate) return 'N/A';
    const begin = new Date(header.reportBeginDate);
    const end = new Date(header.reportEndDate);
    return `${begin.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  };

  const handleSave = async () => {
    try {
      const updated = await svc.updateHeader(header.id, {
        reportBeginDate: editForm.reportBeginDate,
        reportEndDate: editForm.reportEndDate,
        reportToLocation: editForm.reportToLocation,
        reportToPerson: editForm.reportToPerson,
      });
      onHeaderUpdate(updated);
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating header:', err);
      // TODO: Show error toast
    }
  };

  const handleCancel = () => {
    setEditForm({
      reportBeginDate: header.reportBeginDate || '',
      reportEndDate: header.reportEndDate || '',
      reportToLocation: header.reportToLocation,
      reportToPerson: header.reportToPerson,
    });
    setIsEditing(false);
  };

  return (
    <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="container mx-auto px-4 pt-1.5 pb-2">
        <div className="flex items-center justify-between flex-wrap gap-4">
          {/* Left: Job Info */}
          <div className="flex items-center gap-4 flex-wrap">
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Job ID:</span>
              <span className="ml-2 font-semibold dark:text-white">{header.jobId}</span>
            </div>
            <div className="text-gray-400">|</div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Week:</span>
              <span className="ml-2 dark:text-white">{formatDateRange()}</span>
            </div>
            <div className="text-gray-400">|</div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Location:</span>
              <span className="ml-2 dark:text-white">{header.reportToLocation}</span>
            </div>
            <div className="text-gray-400">|</div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Report-to:</span>
              <span className="ml-2 dark:text-white">{header.reportToPerson}</span>
            </div>
          </div>

          {/* Right: Status & Actions */}
          <div className="flex items-center gap-3">
            <Badge color={header.status === 'Submitted' ? 'success' : 'warning'}>
              {header.status}
            </Badge>
            {!isReadOnly && (
              <>
                {isEditing ? (
                  <>
                    <Button size="sm" color="success" onClick={handleSave}>
                      Save
                    </Button>
                    <Button size="sm" color="gray" onClick={handleCancel}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button size="sm" color="gray" onClick={() => setIsEditing(true)}>
                    Edit Header
                  </Button>
                )}
                <Button size="sm" color="blue">
                  Finalize
                </Button>
              </>
            )}
            <Button size="sm" color="gray" onClick={() => navigate('/reports')}>
              Back to Reports
            </Button>
          </div>
        </div>

        {/* Edit Form (when editing) */}
        {isEditing && !isReadOnly && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="editBeginDate" value="Begin Date" />
                <TextInput
                  id="editBeginDate"
                  type="date"
                  value={editForm.reportBeginDate}
                  onChange={(e) =>
                    setEditForm({ ...editForm, reportBeginDate: e.target.value })
                  }
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="editEndDate" value="End Date" />
                <TextInput
                  id="editEndDate"
                  type="date"
                  value={editForm.reportEndDate}
                  onChange={(e) =>
                    setEditForm({ ...editForm, reportEndDate: e.target.value })
                  }
                  className="mt-1"
                  min={editForm.reportBeginDate}
                />
              </div>
              <div>
                <Label htmlFor="editLocation" value="Location" />
                <TextInput
                  id="editLocation"
                  value={editForm.reportToLocation}
                  onChange={(e) =>
                    setEditForm({ ...editForm, reportToLocation: e.target.value })
                  }
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="editPerson" value="Report-to Person" />
                <TextInput
                  id="editPerson"
                  value={editForm.reportToPerson}
                  onChange={(e) =>
                    setEditForm({ ...editForm, reportToPerson: e.target.value })
                  }
                  className="mt-1"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

